import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import type { AgentChoices } from "./agents";
import type { SendSnapshot, ThreadSender } from "./send";

export interface AgentReadiness { ready: boolean; id?: string }

export function AgentPicker({ sender, disabled, onReady, onSnapshot }: {
  sender: ThreadSender;
  disabled: boolean;
  onReady: (value: AgentReadiness) => void;
  onSnapshot: (value: SendSnapshot) => void;
}) {
  const callbacks = useRef({ onReady, onSnapshot });
  callbacks.current = { onReady, onSnapshot };
  const [choices, setChoices] = useState<AgentChoices>();
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const lifetime = useRef<AbortController | undefined>(undefined);
  const inFlight = useRef(false);
  function show(value: AgentChoices) {
    setChoices(value);
    setSelected("");
    callbacks.current.onReady({ ready: Boolean(value.current) && !value.pending, id: value.current?.id });
  }
  useEffect(() => {
    const controller = new AbortController();
    lifetime.current = controller;
    inFlight.current = false;
    setChoices(undefined); setBusy(false); setError(false);
    callbacks.current.onReady({ ready: false });
    void sender.agents(controller.signal).then((value) => {
      if (!controller.signal.aborted) show(value);
    }, () => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [sender, reload]);

  async function choose(useCurrent = false) {
    if (!choices || disabled || inFlight.current) return;
    const pending = choices.pending;
    const profile = pending
      ? { id: pending.profileId, revision: pending.profileRevision, name: "Saved agent" }
      : choices.profiles.find((item) => `${item.id}:${item.revision}` === selected);
    if (!useCurrent && !profile) return;
    const signal = lifetime.current!.signal;
    inFlight.current = true; setBusy(true); setError(false);
    callbacks.current.onReady({ ready: false });
    try {
      const snapshot = useCurrent ? await sender.useCurrentAgent(signal)
        : await sender.chooseAgent(profile!, pending ? pending.previousId : choices.current?.id, signal);
      signal.throwIfAborted();
      callbacks.current.onSnapshot(snapshot);
      const latest = await sender.agents(signal);
      signal.throwIfAborted();
      show(latest);
    } catch {
      if (!signal.aborted) {
        setError(true);
        // Refresh the saved retry, but do not silently resume sending after an
        // uncertain response. The person explicitly reloads or confirms it.
        try {
          const latest = await sender.agents(signal);
          if (!signal.aborted) { setChoices(latest); setSelected(""); }
        } catch { if (!signal.aborted) setChoices(undefined); }
      }
    } finally { if (!signal.aborted) { inFlight.current = false; setBusy(false); } }
  }

  const pending = choices?.pending;
  return <div className="mb-4 text-sm text-[#52606e]" aria-label="Conversation agent">
    {choices ? <p className="mb-2 break-words">{choices.current ? <>Agent: <span className="font-medium text-[#20272b]">{choices.current.profile.name}</span></> : "Choose an agent before sending."}</p> : !error ? <p role="status">Loading agents…</p> : null}
    {error ? <p role="alert" className="mb-2">The agent choice couldn't be confirmed. Your message hasn't been sent.</p> : null}
    {pending ? <div className="flex flex-wrap gap-2">
      <Button variant="ghost" disabled={disabled || busy} onClick={() => void choose()}>Retry agent choice</Button>
      {choices?.current && choices.current.id !== pending.previousId ? <Button variant="ghost" disabled={disabled || busy} onClick={() => void choose(true)}>Use current agent</Button> : null}
    </div> : choices?.profiles.length ? <div className="flex flex-wrap items-center gap-2">
      <label className="sr-only" htmlFor="conversation-agent">Choose agent</label>
      <select id="conversation-agent" value={selected} disabled={disabled || busy} onChange={(event) => {
        setSelected(event.target.value);
        callbacks.current.onReady({ ready: !event.target.value && Boolean(choices.current) && !error, id: choices.current?.id });
      }} className="min-w-0 flex-1 rounded-lg border border-[#cfe4f1] bg-white px-3 py-2 text-[#20272b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]">
        <option value="">{choices.current ? "Change agent…" : "Select an agent…"}</option>
        {choices.profiles.map((item) => <option key={`${item.id}:${item.revision}`} value={`${item.id}:${item.revision}`}>{item.name}</option>)}
      </select>
      <Button variant="ghost" disabled={disabled || busy || !selected} onClick={() => void choose()}>Use agent</Button>
    </div> : choices ? <p>No agents are available to choose in this workspace.</p> : null}
    {error ? <Button className="mt-2" variant="ghost" disabled={disabled || busy} onClick={() => setReload((value) => value + 1)}>Reload agents</Button> : null}
    {busy ? <p role="status" className="mt-2">Saving agent choice…</p> : null}
  </div>;
}
